import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ScrollToTopButton from '../components/ScrollToTopButton';
import BrandLogo from '../components/BrandLogo';
import {
    Sparkles, ArrowLeft, ArrowRight, ArrowDown, Mail, Shield, ShieldCheck,
    Minimize2, Eye, Lock, KeyRound, Clock, Scale,
    CheckCircle, FileText,
} from 'lucide-react';

const BRAND = 'GUIRETAL';
const PARENT = 'NIMVEXA';

// 21 numbered sections rendered under the sticky TOC.
const SECTIONS = [
    'minimization',       // 1. Minimisation
    'purposes',           // 2. Finalités
    'candidates',         // 3. Données candidats
    'responsibilities',   // 4. Responsabilités (with diagram)
    'processors',         // 5. Sous-traitants
    'ai',                 // 6. IA
    'automated',          // 7. Décisions automatisées
    'ocr',                // 8. OCR
    'security',           // 9. Sécurité
    'access',             // 10. Contrôle des accès
    'audit',              // 11. Traçabilité et audit
    'retention',          // 12. Conservation (with table)
    'rights',             // 13. Vos droits
    'exercise',           // 14. Exercer vos droits
    'incidents',          // 15. Gestion des incidents
    'transfers',          // 16. Transferts internationaux
    'dpa',                // 17. DPA (with CTA)
    'more',               // 18. En savoir plus (link to /privacy)
    'clients',            // 19. Responsabilités des orgs clientes
    'responsible',        // 20. Recrutement responsable (checklist)
    'updates',            // 21. Mise à jour
];

const PRINCIPLE_ICONS = [Minimize2, Eye, Lock, KeyRound, Clock, Scale];

const RETENTION_ROWS = [
    'accounts', 'candidates', 'jobs', 'audit_logs', 'billing', 'security',
];

// ----------------------------------------------------------------------------

const renderParagraphs = (items) =>
    Array.isArray(items) && items.length > 0
        ? items.map((p, i) => (
              <p key={i} className="my-3 leading-relaxed text-[#4B5563]">{p}</p>
          ))
        : null;

const renderList = (items) =>
    Array.isArray(items) && items.length > 0 ? (
        <ul className="list-disc pl-6 my-3 space-y-1 text-[#4B5563]">
            {items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
    ) : null;

// ----------------------------------------------------------------------------

export default function GdprPage() {
    const { t, i18n } = useTranslation();
    const [activeSection, setActiveSection] = useState(SECTIONS[0]);

    useEffect(() => {
        document.title = t('gdpr.seo_title');
        const meta = document.querySelector('meta[name="description"]');
        if (meta) meta.setAttribute('content', t('gdpr.seo_description'));
        return () => {
            // Restore the app-wide values in the CURRENT language (not a stale copy).
            document.title = i18n.t('app.document_title');
            if (meta) meta.setAttribute('content', i18n.t('app.document_description'));
        };
    }, [t, i18n.language]);

    useEffect(() => {
        const observers = [];
        SECTIONS.forEach((id) => {
            const el = document.getElementById(`gdpr-section-${id}`);
            if (!el) return;
            const io = new IntersectionObserver(
                (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveSection(id); }),
                { rootMargin: '-40% 0px -55% 0px', threshold: 0 },
            );
            io.observe(el);
            observers.push(io);
        });
        return () => observers.forEach((o) => o.disconnect());
    }, []);

    const toc = useMemo(
        () => SECTIONS.map((id, idx) => ({ id, number: idx + 1, title: t(`gdpr.section_${id}_title`) })),
        [t],
    );

    const principles = t('gdpr.principles', { returnObjects: true, defaultValue: [] });
    const responsibleChecklist = t('gdpr.section_responsible_checklist', { returnObjects: true, defaultValue: [] });

    // Special-case renderers -------------------------------------------------

    const renderResponsibilitiesDiagram = () => (
        <div className="my-6 grid gap-3 max-w-md mx-auto" data-testid="gdpr-responsibilities-diagram">
            <div className="text-center p-5 rounded-xl bg-white border-2 border-[#002FA7] shadow-sm">
                <p className="text-xs uppercase tracking-wider text-[#6B7280] mb-1">{t('gdpr.diagram_org_role')}</p>
                <p className="text-lg font-bold text-[#0A0A0A] font-['Outfit']">{t('gdpr.diagram_org_label')}</p>
            </div>
            <ArrowDown className="w-5 h-5 text-[#002FA7] mx-auto" />
            <div className="text-center p-5 rounded-xl bg-[#0A0A0A] text-white shadow-sm">
                <p className="text-xs uppercase tracking-wider text-white/60 mb-1">{t('gdpr.diagram_platform_role')}</p>
                <p className="text-lg font-bold font-['Outfit']">{BRAND} / {PARENT}</p>
            </div>
            <ArrowDown className="w-5 h-5 text-[#002FA7] mx-auto" />
            <div className="text-center p-5 rounded-xl bg-white border border-[#E5E7EB]">
                <p className="text-xs uppercase tracking-wider text-[#6B7280] mb-1">{t('gdpr.diagram_candidate_role')}</p>
                <p className="text-lg font-bold text-[#0A0A0A] font-['Outfit']">{t('gdpr.diagram_candidate_label')}</p>
            </div>
        </div>
    );

    const renderRetentionTable = () => (
        <div className="my-6 overflow-x-auto rounded-xl border border-[#E5E7EB]" data-testid="gdpr-retention-table">
            <table className="w-full text-sm">
                <thead className="bg-[#F9FAFB] text-[#0A0A0A]">
                    <tr>
                        <th className="text-left px-4 py-3 font-semibold">{t('gdpr.retention_col_category')}</th>
                        <th className="text-left px-4 py-3 font-semibold">{t('gdpr.retention_col_duration')}</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                    {RETENTION_ROWS.map((row) => (
                        <tr key={row}>
                            <td className="px-4 py-3 text-[#0A0A0A] font-medium">{t(`gdpr.retention_row_${row}`)}</td>
                            <td className="px-4 py-3 text-[#6B7280] font-mono text-xs">{t('gdpr.retention_placeholder')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderDpaCta = () => (
        <div className="my-6 p-6 rounded-2xl bg-[#F9FAFB] border border-[#E5E7EB]" data-testid="gdpr-dpa-cta">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-lg bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-1">{t('gdpr.dpa_cta_title')}</h3>
                    <p className="text-sm text-[#4B5563] mb-4">{t('gdpr.dpa_cta_desc')}</p>
                    <Button size="sm" className="bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="gdpr-dpa-contact-btn">
                        <Mail className="w-4 h-4 mr-2" />
                        {t('gdpr.dpa_cta_button')}
                    </Button>
                    <p className="mt-3 text-xs text-[#6B7280] font-mono">{t('gdpr.dpa_cta_email_placeholder')}</p>
                </div>
            </div>
        </div>
    );

    const renderMoreCta = () => (
        <Link to="/privacy" className="inline-block my-4" data-testid="gdpr-privacy-link">
            <Button variant="outline" className="border-[#E5E7EB]">
                {t('gdpr.more_cta_button')}
                <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
        </Link>
    );

    const renderResponsibleChecklist = () => (
        <div className="my-6 rounded-2xl bg-gradient-to-br from-[#002FA7] via-[#002480] to-[#001B5C] text-white p-6 sm:p-8" data-testid="gdpr-responsible-checklist">
            <p className="text-lg sm:text-xl font-bold font-['Outfit'] mb-6 leading-snug">
                {t('gdpr.section_responsible_highlight')}
            </p>
            <ul className="grid sm:grid-cols-2 gap-3">
                {responsibleChecklist.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/90" data-testid={`gdpr-responsible-item-${i}`}>
                        <CheckCircle className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );

    return (
        <div className="min-h-screen bg-white" data-testid="gdpr-page">
            {/* Nav — GUIRETAL branded */}
            <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[#E5E7EB]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="gdpr-nav-home">
                        <BrandLogo size={36} />
                        <span className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">{BRAND}</span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <LanguageSwitcher />
                        <Link to="/">
                            <Button variant="ghost" size="sm" data-testid="gdpr-back-btn">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                {t('gdpr.back_home')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <header className="bg-gradient-to-b from-[#F5F7FF] via-white to-white py-14 sm:py-20 px-4 sm:px-6 border-b border-[#E5E7EB]">
                <div className="max-w-4xl mx-auto text-center">
                    <span
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#002FA7]/10 text-[#002FA7] text-xs font-semibold tracking-wide uppercase mb-6"
                        data-testid="gdpr-hero-badge"
                    >
                        <ShieldCheck className="w-4 h-4" />
                        {t('gdpr.hero_badge')}
                    </span>
                    <h1
                        className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A0A0A] font-['Outfit'] tracking-tight leading-tight"
                        data-testid="gdpr-title"
                    >
                        {t('gdpr.hero_title')}
                    </h1>
                    <p className="mt-4 text-lg text-[#0A0A0A] font-medium">
                        {t('gdpr.hero_subtitle', { brand: BRAND })}
                    </p>
                    <p className="mt-4 text-base sm:text-lg text-[#4B5563] leading-relaxed max-w-3xl mx-auto">
                        {t('gdpr.hero_desc', { brand: BRAND, parent: PARENT })}
                    </p>
                </div>
            </header>

            {/* Approach — 6 principle cards */}
            <section className="py-14 sm:py-20 px-4 sm:px-6 bg-white" data-testid="gdpr-approach">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-12">
                        <p className="text-sm font-semibold uppercase tracking-wider text-[#002FA7] mb-3">
                            {t('gdpr.approach_eyebrow')}
                        </p>
                        <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] font-['Outfit'] tracking-tight">
                            {t('gdpr.approach_title')}
                        </h2>
                        <p className="mt-4 text-[#4B5563] leading-relaxed">
                            {t('gdpr.approach_desc')}
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {principles.map((p, i) => {
                            const Icon = PRINCIPLE_ICONS[i] || Shield;
                            return (
                                <div
                                    key={i}
                                    className="bg-white border border-[#E5E7EB] rounded-xl p-6 hover:border-[#002FA7]/30 hover:shadow-md transition-all"
                                    data-testid={`gdpr-principle-${i + 1}`}
                                >
                                    <div className="w-11 h-11 rounded-lg bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center mb-4">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit'] mb-2">{p.title}</h3>
                                    <p className="text-sm text-[#4B5563] leading-relaxed">{p.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Body: sticky TOC + article */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
                <div className="grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-14">
                    <aside className="hidden lg:block" data-testid="gdpr-toc">
                        <div className="sticky top-24 max-h-[calc(100vh-6rem)] overflow-y-auto pr-4">
                            <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-4">
                                {t('gdpr.toc_title')}
                            </p>
                            <ol className="space-y-1 text-sm">
                                {toc.map(({ id, number, title }) => (
                                    <li key={id}>
                                        <a
                                            href={`#gdpr-section-${id}`}
                                            data-testid={`gdpr-toc-${id}`}
                                            className={`block px-3 py-2 rounded-md transition-colors ${
                                                activeSection === id
                                                    ? 'bg-[#002FA7]/10 text-[#002FA7] font-semibold'
                                                    : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0A0A]'
                                            }`}
                                        >
                                            <span className="text-[#9CA3AF] mr-2">{String(number).padStart(2, '0')}</span>
                                            {title}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </aside>

                    <article
                        className="max-w-3xl text-[#4B5563] leading-relaxed [&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:font-bold [&_h2]:text-[#0A0A0A] [&_h2]:font-['Outfit'] [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:tracking-tight [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#0A0A0A] [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:my-3 [&_ul]:my-3 [&_strong]:text-[#0A0A0A] [&_strong]:font-semibold"
                        data-testid="gdpr-article"
                    >
                        {toc.map(({ id, number, title }) => (
                            <section key={id} id={`gdpr-section-${id}`} data-testid={`gdpr-section-${id}`} className="scroll-mt-24">
                                <h2>{number}. {title}</h2>
                                {renderParagraphs(t(`gdpr.section_${id}_p`, { returnObjects: true, defaultValue: [] }))}
                                {renderList(t(`gdpr.section_${id}_list`, { returnObjects: true, defaultValue: [] }))}

                                {/* Special-case renderers per section */}
                                {id === 'responsibilities' && renderResponsibilitiesDiagram()}
                                {id === 'retention' && renderRetentionTable()}
                                {id === 'dpa' && renderDpaCta()}
                                {id === 'more' && renderMoreCta()}
                                {id === 'responsible' && renderResponsibleChecklist()}

                                {/* Callouts + placeholders + trailing paragraphs */}
                                {t(`gdpr.section_${id}_callout`, { defaultValue: '' }) && (
                                    <p className="my-4 border-l-4 border-[#002FA7] bg-[#F5F7FF] px-4 py-3 text-[#0A0A0A] font-medium">
                                        {t(`gdpr.section_${id}_callout`)}
                                    </p>
                                )}
                                {renderParagraphs(t(`gdpr.section_${id}_placeholders`, { returnObjects: true, defaultValue: [] }))}
                                {renderParagraphs(t(`gdpr.section_${id}_trailing`, { returnObjects: true, defaultValue: [] }))}

                                {/* "OCR" badge visual */}
                                {id === 'ocr' && (
                                    <div className="my-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] text-xs font-semibold" data-testid="gdpr-ocr-badge">
                                        <FileText className="w-3.5 h-3.5" />
                                        {t('gdpr.ocr_badge_label')}
                                    </div>
                                )}
                            </section>
                        ))}

                        {/* Final Contact CTA */}
                        <section className="mt-14 rounded-2xl bg-gradient-to-br from-[#002FA7] to-[#3B82F6] text-white px-6 py-8 sm:px-8 sm:py-10" data-testid="gdpr-final-cta">
                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                                    <Mail className="w-6 h-6" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl sm:text-2xl font-bold font-['Outfit'] mb-2">
                                        {t('gdpr.final_cta_title')}
                                    </h3>
                                    <p className="text-white/85 text-sm sm:text-base mb-4">
                                        {t('gdpr.final_cta_desc')}
                                    </p>
                                    <Button className="bg-white text-[#002FA7] hover:bg-white/90 font-semibold" data-testid="gdpr-final-cta-btn">
                                        {t('gdpr.final_cta_button')}
                                    </Button>
                                    <p className="mt-3 text-white/70 text-sm font-mono" data-testid="gdpr-final-cta-email">
                                        {t('gdpr.final_cta_email_placeholder')}
                                    </p>
                                    <p className="mt-4 text-white/70 text-xs">
                                        {t('gdpr.final_cta_note')}
                                    </p>
                                </div>
                            </div>
                        </section>
                    </article>
                </div>
            </div>

            {/* Footer */}
            <footer className="py-8 px-4 sm:px-6 border-t border-[#E5E7EB] bg-white">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#6B7280]">
                    <div className="flex items-center gap-2">
                        <BrandLogo size={24} rounded="rounded" />
                        <span>{t('landing.footer_copyright')}</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="hover:text-[#0A0A0A]">{t('gdpr.back_home')}</Link>
                        <Link to="/about" className="hover:text-[#0A0A0A]">{t('gdpr.footer_about')}</Link>
                        <Link to="/privacy" className="hover:text-[#0A0A0A]">{t('gdpr.footer_privacy')}</Link>
                        <Link to="/terms" className="hover:text-[#0A0A0A]">{t('gdpr.footer_terms')}</Link>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto text-center text-xs text-[#9CA3AF] italic mt-4">
                    {t('gdpr.footer_last_updated')} {t('gdpr.footer_last_updated_placeholder')}
                </div>
            </footer>

            <ScrollToTopButton />
        </div>
    );
}
